import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../../../components/layout/Layout';
import { useAuthContext } from '../LoginAuthContext';
import eyeSlash from '../../../../images/eye/eye-locked.svg';
import eyeOpen from '../../../../images/eye/eye-open.svg';
import Input from '../../../../components/Input/TextField/Input';
import ErrorMessage from '../../../../components/Error/ErrorMessage/ErrorMessage';
import Button from '../../../../components/Button/Button';
import { useNavigate } from 'react-router-dom';
import { MdOutlineVpnKey } from 'react-icons/md';
import Loading2 from '../../../../components/Utility/LoadingSpinner/Loading';
import { FaEnvelope } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import BrandLogo from '../../../../components/Utility/BrandLogo';
import LoginSidePanel from '../../../../components/layout/Panel/LoginSidePanel';
import { useLoginBrand } from '../useLoginBrand';
import AuthFormCard from '../../../../components/layout/Panel/AuthFormCard';
import AuthLegalFooter from '../components/AuthLegalFooter';
import DevPersonaLogin from '../../../../components/Partials/DevPersonaLogin';
import Oauth2Google from '../../../../pages/0auth20/Oauth2Google';
import { resolveHomePath } from '../../../../constants/navigation';
import { useFeatureFlags } from '../../../feature-flags/presentation/FeatureFlagsContext';
import { hasStoredUserSession } from '../../../../domain/auth/storedUserSelectors';
import { readViewerPreauthToken } from '../browserAuthSessionSupport';
import { useViewerSession } from '../useViewerSession';

export default function Login() {
  let navigate = useNavigate();
  const { storedUser } = useViewerSession();
  const defaultHomePath = useMemo(
    () => resolveHomePath(storedUser),
    [storedUser]
  );

  const {
    loading,
    loading_desc,
    password,
    email,
    hidden,
    fail,
    error_message,
    updateEmail,
    updatePassword,
    toggle_password_visibility,
    setLoading,
    login,
    verifyOtpLogin,
    clearOtpLogin,
    isAuth
  } = useAuthContext();

  const { features } = useFeatureFlags();
  const isGoogleAuthEnabled = Boolean(features?.isGoogleAuthEnabled);

  // Pre-auth workspace branding via ?ws=<uuid> (no-op / Octopus default when absent).
  const loginBrand = useLoginBrand();

  const [devPersona, setDevPersona] = useState(null);

  const [otpToken, setOtpToken] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [otpMode, setOtpMode] = useState(() =>
    Boolean(readViewerPreauthToken())
  );

  useEffect(() => {
    if (hasStoredUserSession(storedUser)) {
      navigate(defaultHomePath, { replace: true });
    }
  }, [defaultHomePath, navigate, storedUser]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true, 'Logging in...');
    if (isFormValid()) {
      const result = await login();
      if (result?.otpRequired) {
        setOtpMode(true);
        setOtpToken('');
      }
    } else {
      setLoading(false); // Stop loading if form is invalid
    }
  };

  const handleOtpVerify = async (event) => {
    event.preventDefault();
    if (!otpToken.trim()) {
      setLoading(false);
      return;
    }
    try {
      await verifyOtpLogin({
        token: otpToken.trim(),
        method: useRecoveryCode ? 'recovery' : 'totp'
      });
    } catch (_) {
    } finally {
      setOtpToken('');
    }
  };

  const handleOtpBack = () => {
    clearOtpLogin();
    setOtpMode(false);
    setOtpToken('');
    setUseRecoveryCode(false);
  };

  const isValidEmail = (email) => {
    // Simple email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFormValid = () => {
    return isValidEmail(email) && password && password.length >= 6;
  };
  if (loading) {
    return <Loading2 message={loading_desc || 'Logging In...'} />;
  }

  return (
    <Layout>
      <div className="flex min-h-screen w-full">
        <LoginSidePanel />
        <div className="relative flex flex-1 flex-col items-center justify-center px-6 py-12 sm:px-12 lg:px-16">
          {/* Dev persona loading overlay */}
          {devPersona && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 dark:bg-black/60 backdrop-blur-sm">
              <Loading2
                overlay={false}
                size={0.5}
                message={`Logging in as ${devPersona.label}...`}
              />
            </div>
          )}

          <div
            className={`w-full ${
              devPersona ? 'blur-sm pointer-events-none select-none' : ''
            }`}
          >
            <div className="flex justify-center md:hidden mb-10">
              <Link to={defaultHomePath}>
                {loginBrand.logoUrl ? (
                  <img
                    src={loginBrand.logoUrl}
                    alt={loginBrand.name || ''}
                    className="h-[140px] w-[160px] object-contain"
                  />
                ) : (
                  <BrandLogo width={160} height={140} />
                )}
              </Link>
            </div>
            {loginBrand.name && (
              <p className="md:hidden -mt-6 mb-10 text-center text-lg font-bold text-dark-gray dark:text-white">
                {loginBrand.name}
              </p>
            )}

            <AuthFormCard
              title={otpMode ? 'Two-factor authentication' : 'Welcome back!'}
              footer={
                <React.Fragment>
                  {otpMode ? (
                    <button
                      type="button"
                      onClick={handleOtpVerify}
                      className="w-full rounded-[50px] h-[58px] text-xl text-white font-bold flex justify-center items-center bg-primary"
                    >
                      <span>Verify</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleLogin}
                      disabled={!isFormValid()}
                      className={`w-full rounded-[50px] h-[58px] text-xl text-white font-bold flex justify-center items-center ${
                        isFormValid()
                          ? 'bg-primary'
                          : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                      }`}
                    >
                      <span>Login</span>
                    </button>
                  )}
                  {!otpMode && isGoogleAuthEnabled && (
                    <div className="pt-4">
                      <div className="flex items-center gap-3 py-2 text-xs uppercase tracking-wider text-gray-400">
                        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                        or
                        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                      </div>
                      <div className="flex justify-center">
                        <Oauth2Google />
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col items-center text-sm text-gray-400 space-y-2 pt-6 md:flex-row md:space-y-0 md:space-x-2 md:justify-center">
                    <span>
                      Forgot your password?
                      <Link
                        to="/identity/reset"
                        className="pl-2 text-black dark:text-white font-bold hover:underline"
                      >
                        Reset
                      </Link>
                    </span>
                    <span className="hidden md:inline">|</span>
                    <span>
                      Not a member?{' '}
                      <Link
                        to="/identity/register"
                        className="pl-2 text-black dark:text-white font-bold hover:underline"
                      >
                        Register
                      </Link>
                    </span>
                  </div>
                </React.Fragment>
              }
            >
              <p className="mb-6 text-sm text-gray-400 tracking-wider text-center">
                {otpMode
                  ? 'Enter the code from your authenticator app.'
                  : 'Sign in to your account.'}
              </p>
              <form
                className="w-full space-y-6"
                onSubmit={otpMode ? handleOtpVerify : handleLogin}
                method="POST"
              >
                {error_message && (
                  <ErrorMessage status={fail} message={error_message} />
                )}
                {otpMode ? (
                  <React.Fragment>
                    <div className="flex items-start gap-4">
                      <span className="mt-4 text-gray-500">
                        <MdOutlineVpnKey />
                      </span>
                      <Input
                        label={
                          useRecoveryCode ? 'Recovery code' : '6-digit code'
                        }
                        required
                        type="text"
                        id="otp"
                        value={otpToken}
                        onChange={(e) => setOtpToken(e.target.value)}
                        wrapperClassName="relative z-0 flex-1 text-left"
                      />
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={useRecoveryCode}
                          onChange={(e) => setUseRecoveryCode(e.target.checked)}
                        />
                        Use a recovery code
                      </label>

                      <Button
                        type="button"
                        onClick={handleOtpBack}
                        pill
                        variant="secondary"
                        size="normal"
                      >
                        Back to login
                      </Button>
                    </div>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <div className="flex items-start gap-4">
                      <span className="mt-4 text-gray-500">
                        <FaEnvelope />
                      </span>
                      <Input
                        label="email"
                        required
                        type="email"
                        id="email"
                        value={email}
                        valid={isValidEmail(email)}
                        invalid={Boolean(email && !isValidEmail(email))}
                        onChange={(e) => updateEmail(e.target.value)}
                        wrapperClassName="relative z-0 flex-1 text-left"
                      />
                    </div>
                    <div className="flex items-start gap-4">
                      <span className="mt-4 text-gray-500">
                        <MdOutlineVpnKey />
                      </span>
                      <div className="relative flex-1">
                        <Input
                          label="Password"
                          required
                          type={hidden ? 'text' : 'password'}
                          id="password"
                          value={password}
                          valid={password && password.length >= 6}
                          onChange={(e) => updatePassword(e.target.value)}
                          wrapperClassName="relative z-0 w-full text-left"
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-3 bg-transparent p-0 border-0"
                          onClick={toggle_password_visibility}
                          aria-label={
                            hidden ? 'Hide password' : 'Show password'
                          }
                        >
                          <img
                            className="w-5 h-5 cursor-pointer opacity-60 hover:opacity-100 dark:invert dark:brightness-200"
                            id="togglePassword"
                            alt={hidden ? 'Hide password' : 'Show password'}
                            srcSet={hidden ? eyeOpen : eyeSlash}
                          />
                        </button>
                      </div>
                    </div>
                  </React.Fragment>
                )}
              </form>
            </AuthFormCard>
          </div>

          <AuthLegalFooter />

          <DevPersonaLogin onSwitching={setDevPersona} />
        </div>
      </div>
    </Layout>
  );
}
